import { Box, Typography, IconButton, Chip, Grid, Card, CardContent, Divider, CircularProgress, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGetMaterialReqDetails } from "../hooks/useGetMaterialReqDetails";

export const RequestDetailsHeader = ({
    projectId,
    requestId,
    onClose
}: {
    projectId: string | undefined;
    requestId: string | null;
    onClose: () => void;
}) => {

    const { data, isFetching, isPending } = useGetMaterialReqDetails({ projectId, requestId });

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase().replace(/_/g, ' ')) {
            case 'order placed':
                return 'primary';
            case 'delivered':
                return 'success';
            case 'pending':
                return 'warning';
            case 'cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                width: '100%',
            }}
        >
            <Box sx={{ flex: 1 }}>
                {(isFetching || isPending) ? (
                    <Typography variant="h6">Loading...</Typography>
                ) : (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {data?.material_ref} - {data?.type_display_text}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Owner: {data?.owner_name || 'N/A'}
                            </Typography>
                            <Chip
                                label={data?.status_display_text || 'N/A'}
                                color={getStatusColor(data?.status || '')}
                                size="small"
                            />
                        </Box>
                    </>
                )}
            </Box>
            <IconButton onClick={onClose} size="small">
                <CloseIcon />
            </IconButton>
        </Box>
    );

};

export const RequestDetails = ({ projectId, requestId }: { projectId: string | undefined, requestId: string | null }) => {

    const { data, isFetching, isPending, isError, error } = useGetMaterialReqDetails({ projectId, requestId });

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    if (isFetching || isPending) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">
                    {error?.message || 'Failed to load material request details'}
                </Alert>
            </Box>
        );
    }

    if (!data) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">No data available</Alert>
            </Box>
        );
    }

    const DetailItem = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
        <Box display={'flex'} justifyContent={'space-between'}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="caption">
                {value || 'N/A'}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ p: 2 }} display={'flex'} flexDirection={'column'} gap={2}>

            {/* General Information */}
            <Grid size={{ xs: 12 }}>
                <Card>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>General Information</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <DetailItem label="Material Reference" value={data.material_ref} />
                        <DetailItem label="Type" value={data.type_display_text} />
                        <DetailItem label="Status" value={data.status_display_text} />
                        <DetailItem label="Description" value={data.description} />
                        <DetailItem label="Quantity" value={data.quantity} />
                        <DetailItem label="Owner" value={data.owner_name} />
                    </CardContent>
                </Card>
            </Grid>

            {/* Project Details */}
            <Grid size={{ xs: 12 }}>
                <Card>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>Project Details</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <DetailItem label="Project" value={data.project_title} />
                        <DetailItem label="Phase" value={data.phase_name} />
                        <DetailItem label="Activity" value={data.activity_name} />
                    </CardContent>
                </Card>
            </Grid>

            {/* Supplier & Delivery */}
            <Grid size={{ xs: 12 }}>
                <Card>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>Supplier & Delivery</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <DetailItem label="Supplier" value={data.supplier_display_text} />
                        <DetailItem label="Point of Contact" value={data.point_of_contact_info} />
                        <DetailItem label="Storage Location" value={data.storage_location} />
                        <DetailItem label="Required Date" value={formatDate(data.required_date)} />
                        <DetailItem label="Order Placed Date" value={formatDate(data.order_placed_date)} />
                        <DetailItem label="Est. Delivery Date" value={formatDate(data.est_delivery_date)} />
                        <DetailItem label="Last Updated Date" value={formatDate(data.last_updated_date)} />
                        <DetailItem label="Created Date" value={formatDate(data.created_date)} />
                        <DetailItem label="Updated Date" value={formatDate(data.updated_date)} />
                    </CardContent>
                </Card>
            </Grid>

            {/* Additional Information */}
            <Grid size={{ xs: 12 }}>
                <Card>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>Additional Information</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <DetailItem label="Last Updated Comments" value={data.last_updated_comments} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

        </Box>
    );

};